import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Handshake, Mail, Users } from "lucide-react";

import { Reveal, SectionHeading, Seo } from "@/components";
import { Container } from "@/layouts";
import { ROUTES, normalizeLanguage } from "@/navigations";
import { absoluteUrl, breadcrumbJsonLd, contactJsonLd } from "@/utils/seo";

import shared from "@/styles/pages.module.css";
import styles from "./styles.module.css";

const PATHS = [
  { key: "students", icon: Users },
  { key: "partners", icon: Handshake },
  { key: "public", icon: Globe },
] as const;

const GetInvolvedPage = memo(() => {
  const { t } = useTranslation("GetInvolvedPage");
  const { t: tSeo } = useTranslation("Seo");
  const { t: tc, i18n } = useTranslation("components");
  const lang = normalizeLanguage(i18n.language);
  const contacts = t("contacts.items", { returnObjects: true }) as {
    name: string;
    role: string;
    email: string;
  }[];

  return (
    <>
      <Seo
        title={tSeo("getInvolved.title")}
        description={tSeo("getInvolved.description")}
        path={ROUTES.GET_INVOLVED}
        jsonLd={[
          contactJsonLd(tSeo("getInvolved.title"), tSeo("getInvolved.description"), absoluteUrl(ROUTES.GET_INVOLVED, lang)),
          breadcrumbJsonLd(
            [
              { name: tc("nav.home"), path: ROUTES.HOME },
              { name: tc("nav.getInvolved"), path: ROUTES.GET_INVOLVED },
            ],
            lang,
          ),
        ]}
      />

      <section className={shared.pageHeader}>
        <Container>
          <Reveal className={shared.pageHeaderInner}>
            <span className="eyebrow" data-reveal>
              {t("hero.eyebrow")}
            </span>
            <h1 className={shared.pageTitle} data-reveal>
              {t("hero.title")}
            </h1>
            <p className={shared.lede} data-reveal>
              {t("hero.lead")}
            </p>
          </Reveal>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.sunken}`}>
        <Container>
          <SectionHeading title={t("paths.title")} />
          <Reveal className={`${shared.grid3} ${shared.stackGap}`}>
            {PATHS.map(({ key, icon: Icon }) => (
              <article key={key} className={styles.pathCard} data-reveal>
                <span className={styles.pathIcon}>
                  <Icon size={20} strokeWidth={1.6} />
                </span>
                <h3 className={styles.pathTitle}>{t(`paths.items.${key}.title`)}</h3>
                <p className={styles.pathBody}>{t(`paths.items.${key}.body`)}</p>
              </article>
            ))}
          </Reveal>
        </Container>
      </section>

      <section className={shared.section}>
        <Container>
          <Reveal>
            <div className={styles.contact} data-reveal>
              <h2 className={styles.contactTitle}>{t("contacts.title")}</h2>
              <p className={styles.contactBody}>{t("contacts.body")}</p>
              <div className={styles.contactGrid}>
                {contacts.map((person) => (
                  <div key={person.email} className={styles.contactPerson}>
                    <p className={styles.contactName}>{person.name}</p>
                    <p className={styles.contactRole}>{person.role}</p>
                    <a className={styles.contactEmail} href={`mailto:${person.email}`}>
                      <Mail size={18} />
                      {person.email}
                    </a>
                  </div>
                ))}
              </div>
              <p className={styles.contactNote}>{t("contacts.note")}</p>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
});

GetInvolvedPage.displayName = "GetInvolvedPage";
export default GetInvolvedPage;
